import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

/**
 * Phase 16: Family Invite Accept Page
 * New member accepts invitation and completes registration
 */
function FamilyInviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [inviteData, setInviteData] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
    language: 'de'
  });
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/family/invite/${token}`);
      setInviteData(response.data);
      setFormData({ ...formData, name: response.data.invited_user_name });
    } catch (err) {
      console.error('Error validating token:', err);
      setError(err.response?.data?.detail || 'Ungültiger oder abgelaufener Einladungslink.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/family/complete-invite`,
        {
          token,
          name: formData.name,
          password: formData.password,
          language: formData.language
        }
      );
      
      // Store token
      localStorage.setItem('family_auth_token', response.data.token);
      
      // Redirect to dashboard
      navigate('/family/dashboard');
    } catch (err) {
      console.error('Error completing invitation:', err);
      setError(err.response?.data?.detail || 'Fehler beim Abschließen der Einladung.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-hayat-primary-50 to-hayat-secondary-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-hayat-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-500 font-medium">Überprüfe Einladung...</p>
        </div>
      </div>
    );
  }

  if (error && !inviteData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-hayat-primary-50 to-hayat-secondary-50 p-4">
        <div className="max-w-md w-full bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-xl border border-neutral-600/40 text-center">
          <div className="text-7xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-[#F9FAFB] mb-2">Ungültige Einladung</h2>
          <p className="text-neutral-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/family/login')}
            className="px-6 py-3 bg-[#5B4B8A] text-white rounded-2xl hover:bg-hayat-primary-600 transition-all font-semibold"
          >
            Zur Anmeldung
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-hayat-primary-50 to-hayat-secondary-50 p-4 page-transition">
      <div className="max-w-2xl w-full bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-xl border border-neutral-600/40 animate-scale-in">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">👋</div>
          <h1 className="text-3xl font-bold text-[#F9FAFB] mb-2">
            Willkommen!
          </h1>
          <p className="text-neutral-500">
            Sie wurden zu <span className="font-bold">{inviteData?.household_name}</span> eingeladen
          </p>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-hayat-danger-50 border border-hayat-danger-200 text-hayat-danger-700 px-6 py-4 rounded-2xl mb-6 animate-fade-in">
            <p className="font-semibold">⚠️ {error}</p>
          </div>
        )}

        {/* Invite Info */}
        <div className="bg-hayat-secondary-50 border border-hayat-secondary-200 px-6 py-4 rounded-2xl mb-6">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{inviteData?.role === 'admin' ? '🔑' : '👤'}</span>
            <div>
              <p className="font-semibold text-hayat-secondary-900">
                Rolle: {inviteData?.role === 'admin' ? 'Administrator' : 'Mitglied'}
              </p>
              <p className="text-sm text-hayat-secondary-700">
                {inviteData?.role === 'admin' 
                  ? 'Sie haben volle Verwaltungsrechte' 
                  : 'Sie können Daten einsehen, aber nicht ändern'}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
              Ihr Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-hayat-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-hayat-primary-500 focus:border-transparent transition-all"
              disabled={submitting}
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
              Passwort
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Mindestens 6 Zeichen"
              className="w-full px-4 py-3 border-2 border-hayat-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-hayat-primary-500 focus:border-transparent transition-all"
              disabled={submitting}
              required
            />
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
              Passwort bestätigen
            </label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              placeholder="Passwort wiederholen"
              className="w-full px-4 py-3 border-2 border-hayat-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-hayat-primary-500 focus:border-transparent transition-all"
              disabled={submitting}
              required
            />
          </div>

          {/* Language */}
          <div>
            <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
              Sprache
            </label>
            <select
              value={formData.language}
              onChange={(e) => setFormData({ ...formData, language: e.target.value })}
              className="w-full px-4 py-3 border-2 border-hayat-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-hayat-primary-500 focus:border-transparent transition-all"
              disabled={submitting}
            >
              <option value="de">🇩🇪 Deutsch</option>
              <option value="en">🇬🇧 English</option>
              <option value="tr">🇹🇷 Türkçe</option>
              <option value="ar">🇸🇦 العربية</option>
            </select>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="w-full py-4 px-6 bg-gradient-to-r from-[#5B4B8A] to-[#4a3d72] text-white rounded-2xl hover:shadow-hayat-lg transition-all font-bold hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={submitting}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Wird abgeschlossen...
              </span>
            ) : (
              'Registrierung abschließen'
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-hayat-neutral-500">
          <p>Durch die Registrierung stimmen Sie den Nutzungsbedingungen zu.</p>
        </div>
      </div>
    </div>
  );
}

export default FamilyInviteAcceptPage;
